import { ROLES } from "@src/constants";
import { WorkloadInformationRenderer } from "@src/custom_renderer/workload_information_renderer";
import { FrameworkComponents } from "@src/types/aggrid_types";
import {
  AllocationRowData,
  WorkloadTableRowData,
} from "@src/types/role_request_types";
import {
  columnTypes,
  groupHeaderStyleOptions,
  preventPinnedColumnJumpingGridOption,
} from "@src/utils/aggrid_utils";
import { getAllocationColumnDefs } from "@src/utils/allocation_column_def_utils";
import { editAllocationValueGetter } from "@src/utils/edit_allocation_utils";
import {
  CellClassParams,
  ColDef,
  GridOptions,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-enterprise";
import { CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";

export function getGridOptions(): GridOptions {
  return {
    ...preventPinnedColumnJumpingGridOption,
    ...groupHeaderStyleOptions,
    rowHeight: 40,
    tooltipShowDelay: 0,
    domLayout: "autoHeight",
    suppressContextMenu: true,
    suppressRowHoverHighlight: true,
    components: getComponents(),
    defaultColDef: {
      ...columnTypes.default,
      resizable: false,
      sortable: false,
      cellClass: "no-focus-border",
    },
    context: {
      view: ROLES.TEAM_LEAD,
    },
  };
}

function getComponents(): FrameworkComponents {
  return {
    workloadInformationRenderer: (props: CustomCellRendererProps) => {
      return <WorkloadInformationRenderer props={props} />;
    },
  };
}

/**
 * Builds the allocation columns for years and months.
 * Starting with the given startDate and ending with the given endDate
 *
 * @param providedStartDate given start date
 * @param providedEndDate given end date
 * @param getColDefMonth given column definition for the month columns
 * @returns column definitions for the workload table
 */
export function getColumnDefs(
  providedStartDate: Date,
  providedEndDate: Date,
  getColDefMonth?: (year: number, month: number) => ColDef
): ColDef[] {
  const startDate: Date = dayjs().isBefore(providedStartDate)
    ? providedStartDate
    : new Date();

  const allocationColumns: ColDef[] = getAllocationColumnDefs(
    startDate,
    providedEndDate,
    getColDefMonth || defaultGetColDefMonth
  );
  return [
    {
      field: "label",
      headerName: "",
      cellRenderer: "workloadInformationRenderer",
      pinned: "left",
      width: 320,
    },
    ...allocationColumns,
  ];
}

function defaultGetColDefMonth(year: number, month: number): ColDef {
  return {
    valueGetter: (params: ValueGetterParams<WorkloadTableRowData>) =>
      editAllocationValueGetter(params, year, month),
    tooltipValueGetter: (params: ITooltipParams) => {
      const foundAllocation: AllocationRowData = params.data.allocations?.get(
        `${month}-${year}`
      );

      if (foundAllocation?.updatedPercentage == null) return null;

      return `The first value was ${foundAllocation.percentage}% and changed into ${foundAllocation.updatedPercentage}%`;
    },
    valueFormatter: (params: ValueFormatterParams) => {
      const foundAllocation: AllocationRowData = params.data.allocations?.get(
        `${month}-${year}`
      );

      return params.value || foundAllocation?.updatedPercentage != null
        ? `${params.value}%`
        : "";
    },
    cellClass: "allocation-cell no-focus-border",
    cellClassRules: {
      // PL updates different workload
      "allocation-updated": (params: CellClassParams): boolean => {
        const foundAllocation: AllocationRowData = params.data.allocations?.get(
          `${month}-${year}`
        );

        return (
          params.node.rowIndex === 0 &&
          foundAllocation?.updatedPercentage != null
        );
      },
      // TL proposes different workload
      "allocation-proposed": (params: CellClassParams): boolean => {
        const foundAllocation: AllocationRowData = params.data.allocations?.get(
          `${month}-${year}`
        );

        return (
          params.node.rowIndex === 1 &&
          foundAllocation?.updatedPercentage != null
        );
      },
    },
  };
}
